import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SANITARIO_SESSIONS_KEY = "sanitario_sessions";
export const SANITARIO_ACTIVE_ID_KEY = "sanitario_active_id";
export const SANITARIO_OSPITE_KEY = "sanitario_ospite_id";

const SanitarioAuthContext = createContext(null);

function loadSessionsFromStorage() {
  try {
    return JSON.parse(localStorage.getItem(SANITARIO_SESSIONS_KEY) || "[]");
  } catch {
    return [];
  }
}

export function SanitarioAuthProvider({ children }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(() => loadSessionsFromStorage());
  const [activeId, setActiveId] = useState(
    () => localStorage.getItem(SANITARIO_ACTIVE_ID_KEY) || null
  );
  const [loading, setLoading] = useState(true);

  const sessionsRef = useRef(sessions);
  const activeIdRef = useRef(activeId);
  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);
  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  const activeOperatore = sessions.find((s) => s.id === activeId) || null;
  const token = activeOperatore?.token || null;

  useEffect(() => {
    localStorage.setItem(SANITARIO_SESSIONS_KEY, JSON.stringify(sessions));
  }, [sessions]);

  useEffect(() => {
    if (activeId) {
      localStorage.setItem(SANITARIO_ACTIVE_ID_KEY, activeId);
    } else {
      localStorage.removeItem(SANITARIO_ACTIVE_ID_KEY);
    }
  }, [activeId]);

  useEffect(() => {
    const storedSessions = loadSessionsFromStorage();
    const storedActiveId = localStorage.getItem(SANITARIO_ACTIVE_ID_KEY);

    if (storedSessions.length === 0) {
      setLoading(false);
      return;
    }

    const targetSession =
      storedSessions.find((s) => s.id === storedActiveId) || storedSessions[0];

    const tryValidate = async () => {
      try {
        await axios.get(`${API_URL}/api/sanitario/me`, {
          headers: { Authorization: `Bearer ${targetSession.token}` },
        });
        setSessions(storedSessions);
        setActiveId(targetSession.id);
      } catch (err) {
        if (err.response?.status === 401 && targetSession.device_token) {
          try {
            const { data } = await axios.post(
              `${API_URL}/api/sanitario/auth/device-login`,
              { device_token: targetSession.device_token }
            );
            const updated = storedSessions.map((s) =>
              s.id === targetSession.id
                ? { ...s, token: data.token, ...data.operatore }
                : s
            );
            setSessions(updated);
            setActiveId(targetSession.id);
          } catch {
            const remaining = storedSessions.filter((s) => s.id !== targetSession.id);
            setSessions(remaining);
            setActiveId(remaining.length > 0 ? remaining[0].id : null);
          }
        } else {
          setSessions(storedSessions);
          setActiveId(targetSession.id);
        }
      } finally {
        setLoading(false);
      }
    };

    tryValidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (res) => res,
      async (err) => {
        const url = err.config?.url || "";
        const isSanitarioApi =
          (url.includes("/api/sanitario/") || url.includes("/api/oss/")) &&
          !url.includes("/sanitario/auth/login") &&
          !url.includes("/sanitario/auth/pin-login") &&
          !url.includes("/sanitario/auth/badge-login") &&
          !url.includes("/sanitario/auth/device-login") &&
          !url.includes("/sanitario/auth/device-logout") &&
          !url.includes("/oss/auth/");

        if (err.response?.status === 401 && isSanitarioApi && !err.config._sanitarioRetry) {
          const activeSess = sessionsRef.current.find(
            (s) => s.id === activeIdRef.current
          );
          if (activeSess?.device_token) {
            err.config._sanitarioRetry = true;
            try {
              const { data } = await axios.post(
                `${API_URL}/api/sanitario/auth/device-login`,
                { device_token: activeSess.device_token }
              );
              setSessions((prev) =>
                prev.map((s) =>
                  s.id === activeSess.id
                    ? { ...s, token: data.token, ...data.operatore }
                    : s
                )
              );
              err.config.headers["Authorization"] = `Bearer ${data.token}`;
              return axios(err.config);
            } catch {
              // fall through
            }
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applySession = useCallback((data) => {
    const newSession = {
      ...data.operatore,
      device_token: data.device_token,
      token: data.token,
    };
    setSessions((prev) => {
      const without = prev.filter((s) => s.id !== newSession.id);
      return [...without, newSession];
    });
    setActiveId(newSession.id);
    return newSession;
  }, []);

  const loginWithPassword = useCallback(
    async (email, password) => {
      const { data } = await axios.post(`${API_URL}/api/sanitario/auth/login`, {
        email,
        password,
      });
      return applySession(data);
    },
    [applySession]
  );

  const loginWithPin = useCallback(
    async ({ pin, email, organizationId }) => {
      const { data } = await axios.post(`${API_URL}/api/sanitario/auth/pin-login`, {
        pin,
        email: email || undefined,
        organization_id: organizationId || undefined,
      });
      return applySession(data);
    },
    [applySession]
  );

  const loginWithBadge = useCallback(
    async ({ badge, organizationId }) => {
      const { data } = await axios.post(`${API_URL}/api/sanitario/auth/badge-login`, {
        badge,
        organization_id: organizationId || undefined,
      });
      return applySession(data);
    },
    [applySession]
  );

  const switchTo = useCallback(
    async (opId) => {
      const targetSession = sessionsRef.current.find((s) => s.id === opId);
      if (!targetSession) return;

      try {
        await axios.get(`${API_URL}/api/sanitario/me`, {
          headers: { Authorization: `Bearer ${targetSession.token}` },
        });
        setActiveId(opId);
        navigate("/sanitario", { replace: true });
      } catch (err) {
        if (err.response?.status === 401 && targetSession.device_token) {
          try {
            const { data } = await axios.post(
              `${API_URL}/api/sanitario/auth/device-login`,
              { device_token: targetSession.device_token }
            );
            setSessions((prev) =>
              prev.map((s) =>
                s.id === opId ? { ...s, token: data.token, ...data.operatore } : s
              )
            );
            setActiveId(opId);
            navigate("/sanitario", { replace: true });
          } catch {
            toast.error("Sessione scaduta, accedi di nuovo.");
            setSessions((prev) => prev.filter((s) => s.id !== opId));
          }
        }
      }
    },
    [navigate]
  );

  const logoutActive = useCallback(async () => {
    const current = sessionsRef.current.find((s) => s.id === activeIdRef.current);
    if (current?.device_token) {
      try {
        await axios.post(`${API_URL}/api/sanitario/auth/device-logout`, {
          device_token: current.device_token,
        });
      } catch {
        // best-effort
      }
    }
    const remaining = sessionsRef.current.filter((s) => s.id !== activeIdRef.current);
    setSessions(remaining);
    if (remaining.length > 0) {
      setActiveId(remaining[0].id);
      navigate("/sanitario", { replace: true });
    } else {
      setActiveId(null);
      navigate("/sanitario-login", { replace: true });
    }
  }, [navigate]);

  const setPin = useCallback(
    async (pin, badgeCode) => {
      if (!token) throw new Error("Non autenticato");
      const { data } = await axios.post(
        `${API_URL}/api/sanitario/me/pin`,
        { pin, badge_code: badgeCode || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSessions((prev) =>
        prev.map((s) =>
          s.id === activeIdRef.current
            ? { ...s, has_pin: true, has_badge: Boolean(badgeCode) || s.has_badge }
            : s
        )
      );
      return data;
    },
    [token]
  );

  return (
    <SanitarioAuthContext.Provider
      value={{
        sessions,
        activeOperatore,
        token,
        loading,
        loginWithPassword,
        loginWithPin,
        loginWithBadge,
        switchTo,
        logoutActive,
        setPin,
      }}
    >
      {children}
    </SanitarioAuthContext.Provider>
  );
}

export function useSanitarioAuth() {
  const ctx = useContext(SanitarioAuthContext);
  if (!ctx) throw new Error("useSanitarioAuth must be used within SanitarioAuthProvider");
  return ctx;
}
